import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Settings, Tag, Building2, Calendar, Layers, FileText, 
  GitBranch, Bell, Shield, Link, Download, Plus, Edit, Trash2,
  Save, Percent, Clock, Users, Mail, Phone, MapPin, FileCode, Check
} from 'lucide-react';
import api from '../lib/api';
import DocumentLayoutSettings from '../components/DocumentLayoutSettings';
import EmailTemplateSettings from '../components/EmailTemplateSettings';
import MigrationCDEPanel from '../components/MigrationCDEPanel';

const SettingsPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  
  // Promozioni
  const [promotions, setPromotions] = useState([]);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [promotionForm, setPromotionForm] = useState({
    name: '',
    description: '',
    discount_percentage: 0,
    applies_to: 'all',
    category_code: '',
    is_active: true
  });

  // Impostazioni Generali
  const [generalSettings, setGeneralSettings] = useState({
    company_name: 'L&F Costruzioni S.r.l.',
    company_form: 'S.r.l.',
    short_name: 'ABITALO',
    vat_number: '',
    registered_office: '',
    operating_office: '',
    legal_representative: '',
    company_email: 'info@abitalo.it',
    company_phone: '',
    company_pec: '',
    company_address: '',
    company_vat: '',
    company_fiscal_code: '',
    default_deposit_percentage: 30,
    session_timeout_minutes: 60,
    smtp_host: 'mail.abitalo.com',
    smtp_port: 465,
    smtp_user: 'noreply@abitalo.com',
    smtp_password: '',  // Mai precompilata con valore reale
    smtp_password_configured: false,  // Flag dal backend
    extra_margin_percentage: 20  // Marginalità Extra Forniture
  });

  // Sale Riunioni
  const [rooms, setRooms] = useState([]);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomForm, setRoomForm] = useState({
    name: '',
    capacity: 4,
    available_from: '09:00',
    available_to: '18:00',
    slot_duration_minutes: 60,
    is_active: true
  });

  // Categorie Preventivo
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    code: '',
    label: '',
    color: '#FF7819',
    order: 0
  });

  // Permessi Dinamici
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsModified, setPermissionsModified] = useState(false);

  // Impostazioni Contrattualistica
  const [contractSettings, setContractSettings] = useState({
    days_after_deposit_for_start: 15,
    sal_review_days: 5,
    invoice_payment_days: 7,
    final_inspection_days: 10,
    termination_delay_days: 30,
    daily_late_payment_penalty: "50,00",
    late_payment_penalty_cap_percent: 10,
    missed_profit_percent: 15,
    non_warranty_site_visit_fee: "150,00",
    default_vat_rate: 10,
    non_consumer_court: "Bari",
    allowed_work_sections: ["Lavori Interni", "Lavori Esterni", "Facciate e Cappotti"],
    excluded_scope_text: ""
  });
  const [contractSettingsLoading, setContractSettingsLoading] = useState(false);

  // Redirect se non admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      toast.error('Accesso non autorizzato');
    }
  }, [isAdmin, navigate]);

  // Carica dati
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [promotionsRes, roomsRes, categoriesRes, settingsRes, permissionsRes, contractSettingsRes] = await Promise.all([
        api.get('/promotions'),
        api.get('/rooms'),
        api.get('/settings/categories'),
        api.get('/settings/general'),
        api.get('/permissions'),
        api.get('/settings/contract-template')
      ]);
      setPromotions(promotionsRes.data || []);
      setRooms(roomsRes.data || []);
      setCategories(categoriesRes.data || []);
      setPermissions(permissionsRes.data || []);
      if (settingsRes.data) {
        // Mappa i campi del backend ai campi del frontend
        const data = settingsRes.data;
        setGeneralSettings(prev => ({ 
          ...prev, 
          ...data,
          // Mappatura campi con nomi diversi
          company_email: data.email || data.company_email || prev.company_email,
          company_phone: data.phone || data.company_phone || prev.company_phone,
          company_pec: data.pec || data.company_pec || prev.company_pec,
          company_address: data.address || data.company_address || prev.company_address,
          company_vat: data.vat_number || data.company_vat || prev.company_vat,
          company_fiscal_code: data.fiscal_code || data.tax_code || data.company_fiscal_code || prev.company_fiscal_code,
          // SICUREZZA: Non precompilare mai smtp_password con valore reale
          // Usa solo il flag smtp_password_configured dal backend
          smtp_password: '',  // Sempre vuoto nel frontend
          smtp_password_configured: data.smtp_password_configured || false,
        }));
      }
      if (contractSettingsRes.data) {
        setContractSettings(prev => ({ ...prev, ...contractSettingsRes.data }));
      }
    } catch (err) {
      toast.error('Errore nel caricamento delle impostazioni');
      if (process.env.NODE_ENV === 'development') {
        console.debug('Settings load error:', err?.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // === PERMESSI ===
  const handlePermissionChange = (featureKey, roleKey, value) => {
    setPermissions(prev => prev.map(perm => {
      if (perm.feature_key === featureKey) {
        return { ...perm, [roleKey]: value };
      }
      return perm;
    }));
    setPermissionsModified(true);
  };

  const handleSavePermissions = async () => {
    setPermissionsLoading(true);
    try {
      await api.put('/permissions', permissions);
      toast.success('Permessi salvati');
      setPermissionsModified(false);
    } catch (error) {
      toast.error('Errore nel salvataggio dei permessi');
    } finally {
      setPermissionsLoading(false);
    }
  };

  // === PROMOZIONI ===
  const handleSavePromotion = async () => {
    try {
      if (editingPromotion) {
        await api.put(`/promotions/${editingPromotion.id}`, promotionForm);
        toast.success('Promozione aggiornata');
      } else {
        await api.post('/promotions', promotionForm);
        toast.success('Promozione creata');
      }
      setShowPromotionModal(false);
      setEditingPromotion(null);
      setPromotionForm({ name: '', description: '', discount_percentage: 0, applies_to: 'all', category_code: '', is_active: true });
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEditPromotion = (promo) => {
    setEditingPromotion(promo);
    setPromotionForm(promo);
    setShowPromotionModal(true);
  };

  const handleDeletePromotion = async (id) => {
    if (!window.confirm('Eliminare questa promozione?')) return;
    try {
      await api.delete(`/promotions/${id}`);
      toast.success('Promozione eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // === IMPOSTAZIONI GENERALI ===
  const handleSaveGeneralSettings = async () => {
    try {
      // Mappa i campi del frontend ai campi del backend
      const dataToSave = {
        ...generalSettings,
        // Mappatura campi con nomi diversi per il backend
        email: generalSettings.company_email,
        phone: generalSettings.company_phone,
        pec: generalSettings.company_pec,
        address: generalSettings.company_address,
        vat_number: generalSettings.company_vat,
        fiscal_code: generalSettings.company_fiscal_code,
        tax_code: generalSettings.company_fiscal_code,
      };
      
      // SICUREZZA: Non inviare smtp_password se vuota (preserva esistente)
      // Rimuovi anche smtp_password_configured (è solo per display)
      delete dataToSave.smtp_password_configured;
      if (!dataToSave.smtp_password || dataToSave.smtp_password.trim() === '') {
        delete dataToSave.smtp_password;
      }
      
      await api.put('/settings/general', dataToSave);
      toast.success('Impostazioni salvate');
      
      // Dopo salvataggio, svuota il campo password locale e ricarica per aggiornare il flag
      setGeneralSettings(prev => ({ ...prev, smtp_password: '' }));
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  // === IMPOSTAZIONI CONTRATTUALISTICA ===
  const handleSaveContractSettings = async () => {
    setContractSettingsLoading(true);
    try {
      await api.put('/settings/contract-template', contractSettings);
      toast.success('Impostazioni contratto salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio delle impostazioni contratto');
    } finally {
      setContractSettingsLoading(false);
    }
  };

  // === SALE RIUNIONI ===
  const handleSaveRoom = async () => {
    try {
      if (editingRoom) {
        await api.put(`/rooms/${editingRoom.id}`, roomForm);
        toast.success('Sala aggiornata');
      } else {
        await api.post('/rooms', roomForm);
        toast.success('Sala creata');
      }
      setShowRoomModal(false);
      setEditingRoom(null);
      setRoomForm({ name: '', capacity: 4, available_from: '09:00', available_to: '18:00', slot_duration_minutes: 60, is_active: true });
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room);
    setRoomForm(room);
    setShowRoomModal(true);
  };

  const handleDeleteRoom = async (id) => {
    if (!window.confirm('Eliminare questa sala?')) return;
    try {
      await api.delete(`/rooms/${id}`);
      toast.success('Sala eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // === CATEGORIE ===
  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await api.put(`/settings/categories/${editingCategory.code}`, categoryForm);
        toast.success('Categoria aggiornata');
      } else {
        await api.post('/settings/categories', categoryForm);
        toast.success('Categoria creata');
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ code: '', label: '', color: '#FF7819', order: 0 });
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm(cat);
    setShowCategoryModal(true);
  };

  // === EXPORT ===
  const handleExport = async (type) => {
    try {
      const response = await api.get(`/settings/export/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export completato');
    } catch (error) {
      toast.error('Errore nell\'export');
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-[#FF7819] to-[#FF9A4D] rounded-xl flex items-center justify-center">
          <Settings className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-h2">Impostazioni</h1>
          <p className="text-body-small">Configura il tuo CRM</p>
        </div>
      </div>

      {/* PANNELLO TEMPORANEO — Migrazione listino C/D/E Production (rimuovere dopo l'esecuzione) */}
      <MigrationCDEPanel />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="general" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Building2 className="w-4 h-4 mr-2" />
            Generali
          </TabsTrigger>
          <TabsTrigger value="rooms" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Calendar className="w-4 h-4 mr-2" />
            Sale Riunioni
          </TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Layers className="w-4 h-4 mr-2" />
            Categorie
          </TabsTrigger>
          <TabsTrigger value="workflow" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <GitBranch className="w-4 h-4 mr-2" />
            Workflow
          </TabsTrigger>
          <TabsTrigger value="promotions" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Tag className="w-4 h-4 mr-2" />
            Promozioni
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Bell className="w-4 h-4 mr-2" />
            Notifiche
          </TabsTrigger>
          <TabsTrigger value="permissions" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Shield className="w-4 h-4 mr-2" />
            Permessi
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Percent className="w-4 h-4 mr-2" />
            Pagamenti
          </TabsTrigger>
          <TabsTrigger value="integrations" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Link className="w-4 h-4 mr-2" />
            Integrazioni
          </TabsTrigger>
          <TabsTrigger value="layout" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <FileCode className="w-4 h-4 mr-2" />
            Layout Documenti
          </TabsTrigger>
          <TabsTrigger value="contracts" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Contrattualistica
          </TabsTrigger>
          <TabsTrigger value="email-templates" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Mail className="w-4 h-4 mr-2" />
            Template Email
          </TabsTrigger>
          <TabsTrigger value="export" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Download className="w-4 h-4 mr-2" />
            Export
          </TabsTrigger>
          <TabsTrigger value="operations" className="data-[state=active]:bg-[#FF7819] data-[state=active]:text-white">
            <Settings className="w-4 h-4 mr-2" />
            Operazioni
          </TabsTrigger>
        </TabsList>

        {/* PROMOZIONI */}
        <TabsContent value="promotions" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Gestione Promozioni</CardTitle>
                <CardDescription>Crea e gestisci le promozioni applicabili ai preventivi</CardDescription>
              </div>
              <Button className="btn-primary" onClick={() => { setEditingPromotion(null); setPromotionForm({ name: '', description: '', discount_percentage: 0, applies_to: 'all', category_code: '', is_active: true }); setShowPromotionModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuova Promozione
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {promotions.length === 0 ? (
                  <p className="text-center text-[#737373] py-8">Nessuna promozione configurata</p>
                ) : (
                  promotions.map(promo => (
                    <div key={promo.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#FF7819] to-[#FF9A4D] rounded-lg flex items-center justify-center">
                          <Percent className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-medium">{promo.name}</p>
                          <p className="text-sm text-[#737373]">{promo.description || `Sconto ${promo.discount_percentage}%`}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant={promo.is_active ? 'default' : 'secondary'}>
                              {promo.is_active ? 'Attiva' : 'Disattivata'}
                            </Badge>
                            <Badge variant="outline">
                              {promo.applies_to === 'all' ? 'Tutti i prodotti' : `Categoria ${promo.category_code}`}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-2xl font-bold text-[#FF7819]">-{promo.discount_percentage}%</span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEditPromotion(promo)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeletePromotion(promo.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* IMPOSTAZIONI GENERALI */}
        <TabsContent value="general" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Dati Aziendali per Contratti</CardTitle>
              <CardDescription>Informazioni dell'azienda visualizzate nei contratti e documenti legali</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ragione Sociale Completa *</Label>
                  <Input 
                    value={generalSettings.company_name} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="L&F Costruzioni S.r.l."
                  />
                  <p className="text-xs text-[#737373]">Es: L&F Costruzioni S.r.l.</p>
                </div>
                <div className="space-y-2">
                  <Label>Forma Giuridica</Label>
                  <Input 
                    value={generalSettings.company_form} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, company_form: e.target.value }))}
                    placeholder="S.r.l."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nome Breve / Brand</Label>
                  <Input 
                    value={generalSettings.short_name} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, short_name: e.target.value }))}
                    placeholder="ABITALO"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Partita IVA *</Label>
                  <Input 
                    value={generalSettings.vat_number} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, vat_number: e.target.value }))}
                    placeholder="IT12345678901"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Sede Legale *</Label>
                  <Input 
                    value={generalSettings.registered_office} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, registered_office: e.target.value }))}
                    placeholder="Via Roma 1, 70100 Bari (BA)"
                    data-testid="settings-registered-office"
                  />
                  <p className="text-xs text-[#737373]">Sede legale usata nella copertina e nella parte narrativa dei contratti (TRA / E).</p>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Sede Operativa</Label>
                  <Input 
                    value={generalSettings.operating_office || ''} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, operating_office: e.target.value }))}
                    placeholder="Via Pacifico 59, 70010 Capurso (BA)"
                    data-testid="settings-operating-office"
                  />
                  <p className="text-xs text-[#737373]">Sede operativa mostrata nel footer istituzionale dei PDF contrattuali. Se vuota, viene usata la sede legale.</p>
                </div>
                {(() => {
                  const reg = (generalSettings.registered_office || '').trim();
                  const ope = (generalSettings.operating_office || '').trim();
                  const regLower = reg.toLowerCase();
                  const opeLower = ope.toLowerCase();
                  const missingRegistered = reg === '';
                  const missingOperating = ope === '';
                  const identical = reg !== '' && ope !== '' && regLower === opeLower;
                  // Inversione legacy nota: registered contiene "Via Pacifico" e operating contiene "Via Epifania"
                  const legacyInverted =
                    regLower.includes('via pacifico') && opeLower.includes('via epifania');
                  const invalid = missingRegistered || missingOperating || identical || legacyInverted;
                  const legacyCount = generalSettings._legacy_office_fallback_count || 0;
                  const legacyLastAt = generalSettings._legacy_office_fallback_last_at;
                  const formattedLastAt = legacyLastAt
                    ? (() => {
                        try {
                          return new Date(legacyLastAt).toLocaleString('it-IT');
                        } catch (_) {
                          return String(legacyLastAt);
                        }
                      })()
                    : null;

                  if (invalid) {
                    return (
                      <div
                        className="md:col-span-2 flex items-start gap-3 rounded border-2 border-rose-300 bg-rose-50 p-3"
                        data-testid="settings-office-configuration-invalid-banner"
                      >
                        <span className="text-rose-600 text-xl leading-none">⚠️</span>
                        <div className="text-sm text-rose-900">
                          <div className="font-semibold">
                            Configurazione sedi incompleta o incoerente. Verificare Sede Legale e Sede Operativa.
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (legacyCount > 0) {
                    return (
                      <div
                        className="md:col-span-2 text-xs text-[#737373]"
                        data-testid="settings-legacy-office-fallback-note"
                      >
                        Il fallback legacy delle sedi è stato utilizzato in passato <strong>{legacyCount}</strong>{' '}
                        {legacyCount === 1 ? 'volta' : 'volte'}
                        {formattedLastAt ? <>. Ultimo utilizzo: <strong>{formattedLastAt}</strong></> : null}.
                      </div>
                    );
                  }

                  return null;
                })()}
                <div className="space-y-2 md:col-span-2">
                  <Label>Legale Rappresentante *</Label>
                  <Input 
                    value={generalSettings.legal_representative} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, legal_representative: e.target.value }))}
                    placeholder="Nome Cognome"
                  />
                  <p className="text-xs text-[#737373]">Nome del legale rappresentante che firmerà i contratti</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Contatti Aziendali</CardTitle>
              <CardDescription>Informazioni di contatto per comunicazioni</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={generalSettings.company_email} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, company_email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefono</Label>
                  <Input 
                    value={generalSettings.company_phone} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, company_phone: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>PEC</Label>
                  <Input 
                    type="email"
                    data-testid="settings-company-pec-input"
                    value={generalSettings.company_pec} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, company_pec: e.target.value }))}
                    placeholder="azienda@pec.it"
                  />
                  <p className="text-xs text-[#737373]">Usata nei contratti di subappalto. Se vuota, la riga PEC viene omessa dal PDF</p>
                </div>
                <div className="space-y-2">
                  <Label>P.IVA (vecchio campo)</Label>
                  <Input 
                    value={generalSettings.company_vat} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, company_vat: e.target.value }))}
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-xs text-[#737373]">Usa il campo "Partita IVA" sopra</p>
                </div>
                <div className="space-y-2">
                  <Label>Codice Fiscale</Label>
                  <Input 
                    value={generalSettings.company_fiscal_code} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, company_fiscal_code: e.target.value }))}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Indirizzo Operativo</Label>
                  <Input 
                    value={generalSettings.company_address} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, company_address: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Parametri di Default</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Percentuale Acconto Default (%)</Label>
                  <Input 
                    type="number"
                    min="0"
                    max="100"
                    value={generalSettings.default_deposit_percentage} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, default_deposit_percentage: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Timeout Sessione (minuti)</Label>
                  <Input 
                    type="number"
                    min="15"
                    value={generalSettings.session_timeout_minutes} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, session_timeout_minutes: parseInt(e.target.value) || 60 }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Marginalità Extra Forniture (%)</Label>
                  <Input 
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={generalSettings.extra_margin_percentage} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, extra_margin_percentage: parseFloat(e.target.value) || 20 }))}
                  />
                  <p className="text-xs text-[#737373]">
                    Percentuale da aggiungere automaticamente ai prezzi fornitore per gli Extra
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Configurazione Email SMTP</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Host SMTP</Label>
                  <Input 
                    value={generalSettings.smtp_host} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Porta</Label>
                  <Input 
                    type="number"
                    value={generalSettings.smtp_port} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 465 }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input 
                    value={generalSettings.smtp_user} 
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                  />
                </div>
              </div>
              
              {/* SMTP Password - Campo mascherato */}
              <div className="space-y-2">
                <Label>Password SMTP</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    type="password"
                    value={generalSettings.smtp_password}
                    placeholder={generalSettings.smtp_password_configured ? "Password configurata" : "Inserisci password SMTP"}
                    onChange={(e) => setGeneralSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                    className="max-w-md"
                  />
                  {generalSettings.smtp_password_configured && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Configurata
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#737373]">
                  {generalSettings.smtp_password_configured 
                    ? "Lascia vuoto per mantenere la password esistente. Inserisci un nuovo valore per aggiornarla."
                    : "Inserisci la password per abilitare l'invio email."}
                </p>
              </div>
              
              <Button className="btn-primary" onClick={handleSaveGeneralSettings}>
                <Save className="w-4 h-4 mr-2" />
                Salva Impostazioni
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SALE RIUNIONI */}
        <TabsContent value="rooms" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Sale Riunioni</CardTitle>
                <CardDescription>Configura le sale disponibili per gli appuntamenti</CardDescription>
              </div>
              <Button className="btn-primary" onClick={() => { setEditingRoom(null); setRoomForm({ name: '', capacity: 4, available_from: '09:00', available_to: '18:00', slot_duration_minutes: 60, is_active: true }); setShowRoomModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuova Sala
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {rooms.length === 0 ? (
                  <p className="text-center text-[#737373] py-8 col-span-2">Nessuna sala configurata</p>
                ) : (
                  rooms.map(room => (
                    <Card key={room.id} className="bg-gray-50">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{room.name}</h3>
                            <div className="mt-2 space-y-1 text-sm text-[#737373]">
                              <p className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                Capacità: {room.capacity} persone
                              </p>
                              <p className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                {room.available_from} - {room.available_to}
                              </p>
                              <p className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Slot: {room.slot_duration_minutes} minuti
                              </p>
                            </div>
                            <Badge className="mt-2" variant={room.is_active ? 'default' : 'secondary'}>
                              {room.is_active ? 'Attiva' : 'Disattivata'}
                            </Badge>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditRoom(room)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteRoom(room.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CATEGORIE PREVENTIVO */}
        <TabsContent value="categories" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Macro-categorie Preventivo</CardTitle>
                <CardDescription>Gestisci le categorie del preventivatore</CardDescription>
              </div>
              <Button className="btn-primary" onClick={() => { setEditingCategory(null); setCategoryForm({ code: '', label: '', color: '#FF7819', order: 0 }); setShowCategoryModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Nuova Categoria
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {categories.length === 0 ? (
                  <p className="text-center text-[#737373] py-8">Nessuna categoria configurata</p>
                ) : (
                  categories.map(cat => (
                    <div 
                      key={cat.code} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => handleEditCategory(cat)}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white"
                          style={{ backgroundColor: cat.color || '#FF7819' }}
                        >
                          {cat.code}
                        </div>
                        <span className="font-medium hover:text-[#FF7819] transition-colors">
                          {cat.name || cat.label}
                        </span>
                      </div>
                      <Edit className="w-4 h-4 text-[#737373]" />
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WORKFLOW */}
        <TabsContent value="workflow" className="space-y-4">
          <WorkflowSettings />
        </TabsContent>

        {/* NOTIFICHE */}
        <TabsContent value="notifications" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Configurazione Notifiche Email</CardTitle>
              <CardDescription>Scegli quando inviare notifiche automatiche</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'new_lead', label: 'Nuovo Lead', desc: 'Notifica PM quando arriva un nuovo lead' },
                { key: 'appointment_reminder', label: 'Promemoria Appuntamento', desc: 'Invia promemoria 24h prima dell\'appuntamento' },
                { key: 'estimate_submitted', label: 'Preventivo Inviato', desc: 'Notifica Admin quando un preventivo viene inviato per validazione' },
                { key: 'contract_approved', label: 'Contratto Approvato', desc: 'Notifica Tecnico e Amministrazione quando il contratto è approvato' },
                { key: 'deposit_received', label: 'Acconto Ricevuto', desc: 'Notifica PM quando l\'acconto viene confermato' },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-[#737373]">{item.desc}</p>
                  </div>
                  <Switch defaultChecked />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PERMESSI */}
        <TabsContent value="permissions" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Permessi Ruoli</CardTitle>
                  <CardDescription>Configura cosa può fare ogni ruolo</CardDescription>
                </div>
                {permissionsModified && (
                  <Button onClick={handleSavePermissions} disabled={permissionsLoading}>
                    <Save className="w-4 h-4 mr-2" />
                    {permissionsLoading ? 'Salvataggio...' : 'Salva Modifiche'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-2 font-medium">Funzionalità</th>
                      <th className="text-center py-3 px-2 font-medium">Admin</th>
                      <th className="text-center py-3 px-2 font-medium">PM</th>
                      <th className="text-center py-3 px-2 font-medium">Amministraz.</th>
                      <th className="text-center py-3 px-2 font-medium">Tecnico</th>
                      <th className="text-center py-3 px-2 font-medium">Vend. Arredi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissions.map((perm, idx) => (
                      <tr key={perm.feature_key || idx} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2">{perm.feature_name}</td>
                        <td className="text-center py-3 px-2">
                          <Switch 
                            checked={perm.admin} 
                            onCheckedChange={(v) => handlePermissionChange(perm.feature_key, 'admin', v)}
                            disabled={perm.feature_key === 'view_settings'} // Admin sempre può vedere impostazioni
                          />
                        </td>
                        <td className="text-center py-3 px-2">
                          <Switch 
                            checked={perm.project_manager} 
                            onCheckedChange={(v) => handlePermissionChange(perm.feature_key, 'project_manager', v)}
                          />
                        </td>
                        <td className="text-center py-3 px-2">
                          <Switch 
                            checked={perm.administration} 
                            onCheckedChange={(v) => handlePermissionChange(perm.feature_key, 'administration', v)}
                          />
                        </td>
                        <td className="text-center py-3 px-2">
                          <Switch 
                            checked={perm.external_technician} 
                            onCheckedChange={(v) => handlePermissionChange(perm.feature_key, 'external_technician', v)}
                          />
                        </td>
                        <td className="text-center py-3 px-2">
                          <Switch 
                            checked={perm.furniture_sales} 
                            onCheckedChange={(v) => handlePermissionChange(perm.feature_key, 'furniture_sales', v)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {permissions.length === 0 && (
                <p className="text-center py-8 text-gray-500">Caricamento permessi...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INTEGRAZIONI */}
        <TabsContent value="integrations" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Integrazioni Esterne</CardTitle>
              <CardDescription>Connetti il CRM con altri servizi</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Link className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">TeamLeader Focus</p>
                      <p className="text-sm text-[#737373]">Sincronizza lead e contatti</p>
                    </div>
                  </div>
                  <Badge variant="outline">Non configurato</Badge>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Link className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">Make (Integromat)</p>
                      <p className="text-sm text-[#737373]">Automazioni e webhook</p>
                    </div>
                  </div>
                  <Badge variant="outline">Non configurato</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAGAMENTI */}
        <TabsContent value="payments" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5 text-[#FF7819]" />
                Fasce di Pagamento
              </CardTitle>
              <CardDescription>
                Configura le fasce di importo e le relative milestone di pagamento.
                Ogni preventivo verrà associato automaticamente alla fascia corretta in base all'importo totale.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-[#737373]">
                  Le fasce di pagamento determinano automaticamente le modalità di pagamento in base all'importo del preventivo.
                  Per esempio: preventivi sotto €10.000 potrebbero avere 2 rate, mentre quelli sopra €50.000 potrebbero avere 4 rate con SAL.
                </p>
                <Button 
                  className="btn-primary"
                  onClick={() => window.location.href = '/settings/admin/payment-settings'}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configura Fasce di Pagamento
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LAYOUT DOCUMENTI */}
        <TabsContent value="layout" className="space-y-4">
          <DocumentLayoutSettings />
        </TabsContent>

        {/* CONTRATTUALISTICA */}
        <TabsContent value="contracts" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#FF7819]" />
                Impostazioni Template Contratto di Appalto
              </CardTitle>
              <CardDescription>
                Configura i parametri di default per la generazione dei contratti di appalto.
                Questi valori verranno utilizzati come default ma potranno essere modificati per singolo contratto.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Sezioni Lavoro Consentite */}
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="font-semibold text-[#3C3C3C] mb-2">Sezioni Lavoro Consentite (Contratto Primo Step)</h4>
                <p className="text-sm text-[#737373] mb-3">
                  Il contratto "Primo Step - Opere Edili" include SOLO queste sezioni. Finiture, arredi e forniture sono escluse.
                </p>
                <div className="flex flex-wrap gap-2">
                  {contractSettings.allowed_work_sections?.map((section, idx) => (
                    <Badge key={idx} className="bg-[#FF7819] text-white">{section}</Badge>
                  ))}
                </div>
              </div>

              {/* Testo Esclusioni */}
              <div className="space-y-2">
                <Label className="font-semibold">Testo Esclusioni dal Contratto</Label>
                <p className="text-xs text-[#737373]">
                  Questo testo appare nel contratto per specificare cosa è ESCLUSO (finiture, arredi, serramenti, ecc.)
                </p>
                <Textarea
                  value={contractSettings.excluded_scope_text || ''}
                  onChange={(e) => setContractSettings(prev => ({ ...prev, excluded_scope_text: e.target.value }))}
                  rows={4}
                  placeholder="Sono espressamente esclusi dal presente contratto..."
                />
              </div>

              {/* Parametri Tempistiche */}
              <div>
                <h4 className="font-semibold text-[#3C3C3C] mb-3">Tempistiche e Termini</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Giorni dopo acconto per inizio lavori</Label>
                    <Input
                      type="number"
                      value={contractSettings.days_after_deposit_for_start || 15}
                      onChange={(e) => setContractSettings(prev => ({ ...prev, days_after_deposit_for_start: parseInt(e.target.value) || 15 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Giorni per verifica SAL</Label>
                    <Input
                      type="number"
                      value={contractSettings.sal_review_days || 5}
                      onChange={(e) => setContractSettings(prev => ({ ...prev, sal_review_days: parseInt(e.target.value) || 5 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Giorni per pagamento fattura</Label>
                    <Input
                      type="number"
                      value={contractSettings.invoice_payment_days || 7}
                      onChange={(e) => setContractSettings(prev => ({ ...prev, invoice_payment_days: parseInt(e.target.value) || 7 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Giorni per verifica finale</Label>
                    <Input
                      type="number"
                      value={contractSettings.final_inspection_days || 10}
                      onChange={(e) => setContractSettings(prev => ({ ...prev, final_inspection_days: parseInt(e.target.value) || 10 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Giorni ritardo per risoluzione</Label>
                    <Input
                      type="number"
                      value={contractSettings.termination_delay_days || 30}
                      onChange={(e) => setContractSettings(prev => ({ ...prev, termination_delay_days: parseInt(e.target.value) || 30 }))}
                    />
                  </div>
                </div>
              </div>

              {/* Parametri Penali */}
              <div>
                <h4 className="font-semibold text-[#3C3C3C] mb-3">Penali e Clausole Economiche</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Penale giornaliera ritardo pagamento (€)</Label>
                    <Input
                      value={contractSettings.daily_late_payment_penalty || "50,00"}
                      onChange={(e) => setContractSettings(prev => ({ ...prev, daily_late_payment_penalty: e.target.value }))}
                      placeholder="50,00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cap penale ritardo (%)</Label>
                    <Input
                      type="number"
                      value={contractSettings.late_payment_penalty_cap_percent || 10}
                      onChange={(e) => setContractSettings(prev => ({ ...prev, late_payment_penalty_cap_percent: parseInt(e.target.value) || 10 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mancato guadagno recesso (%)</Label>
                    <Input
                      type="number"
                      value={contractSettings.missed_profit_percent || 15}
                      onChange={(e) => setContractSettings(prev => ({ ...prev, missed_profit_percent: parseInt(e.target.value) || 15 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Costo sopralluogo non in garanzia (€)</Label>
                    <Input
                      value={contractSettings.non_warranty_site_visit_fee || "150,00"}
                      onChange={(e) => setContractSettings(prev => ({ ...prev, non_warranty_site_visit_fee: e.target.value }))}
                      placeholder="150,00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>IVA default (%)</Label>
                    <Input
                      type="number"
                      value={contractSettings.default_vat_rate || 10}
                      onChange={(e) => setContractSettings(prev => ({ ...prev, default_vat_rate: parseInt(e.target.value) || 10 }))}
                    />
                  </div>
                </div>
              </div>

              {/* Foro Competente */}
              <div className="space-y-2">
                <Label className="font-semibold">Foro Competente (per non consumatori)</Label>
                <Input
                  value={contractSettings.non_consumer_court || "Bari"}
                  onChange={(e) => setContractSettings(prev => ({ ...prev, non_consumer_court: e.target.value }))}
                  placeholder="Bari"
                />
                <p className="text-xs text-[#737373]">
                  Per i consumatori si applica automaticamente il foro del luogo di residenza.
                </p>
              </div>

              {/* Salva */}
              <div className="flex justify-end pt-4 border-t">
                <Button 
                  className="btn-primary" 
                  onClick={handleSaveContractSettings}
                  disabled={contractSettingsLoading}
                >
                  {contractSettingsLoading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Salva Impostazioni Contratto
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* EMAIL TEMPLATES */}
        <TabsContent value="email-templates" className="space-y-4">
          <EmailTemplateSettings />
        </TabsContent>

        {/* EXPORT */}
        <TabsContent value="export" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Backup & Export Dati</CardTitle>
              <CardDescription>Esporta i dati del CRM in formato CSV</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { type: 'leads', label: 'Lead', icon: Users },
                  { type: 'estimates', label: 'Preventivi', icon: FileText },
                  { type: 'contracts', label: 'Contratti', icon: FileText },
                  { type: 'projects', label: 'Progetti', icon: Building2 },
                ].map(item => (
                  <Button 
                    key={item.type} 
                    variant="outline" 
                    className="h-auto py-6 flex-col gap-2"
                    onClick={() => handleExport(item.type)}
                  >
                    <item.icon className="w-8 h-8 text-[#FF7819]" />
                    <span>Esporta {item.label}</span>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* OPERAZIONI MASSIVE */}
        <TabsContent value="operations" className="space-y-4">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Operazioni Massive sui Preventivi</CardTitle>
              <CardDescription>
                Applica modifiche a tutti i preventivi esistenti
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Auto-insert Progettazione */}
              <div className="border border-[#DCDCDC] rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-medium text-lg">Inserisci Voci Progettazione (Categoria A)</h3>
                    <p className="text-sm text-[#737373] mt-1">
                      Aggiunge automaticamente tutte le voci della categoria <strong>A - PROGETTAZIONE</strong> 
                      a tutti i preventivi esistenti che non le hanno, con sconto del <strong>100%</strong> (omaggio aziendale).
                    </p>
                    <ul className="text-sm text-[#737373] mt-2 list-disc list-inside">
                      <li>Le voci vengono prese dal Listino Prezzi (categoria A)</li>
                      <li>Quantità impostata a 1</li>
                      <li>Sconto 100% automatico (modificabile dall'operatore)</li>
                      <li>Visibile nel preventivo come "Omaggio aziendale"</li>
                    </ul>
                  </div>
                  <Button 
                    className="bg-[#FF7819] hover:bg-[#E56A10] text-white shrink-0"
                    onClick={async () => {
                      if (!window.confirm('Sei sicuro di voler aggiungere le voci di Progettazione a TUTTI i preventivi esistenti?\n\nQuesta operazione non può essere annullata.')) return;
                      try {
                        setLoading(true);
                        const { estimateApi } = await import('../lib/api');
                        const res = await estimateApi.addProgettazioneToAll();
                        toast.success(`Operazione completata! Aggiornati ${res.data.updated} preventivi, saltati ${res.data.skipped}`);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Errore nell\'operazione');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    <Layers className="w-4 h-4 mr-2" />
                    Applica a Tutti i Preventivi
                  </Button>
                </div>
              </div>

              <div className="text-sm text-[#737373] bg-amber-50 border border-amber-200 rounded-lg p-3">
                <strong>Nota:</strong> I nuovi preventivi creati includeranno automaticamente le voci di Progettazione 
                con sconto 100%. Gli operatori possono rimuoverle o modificare lo sconto se necessario.
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Promozione */}
      <Dialog open={showPromotionModal} onOpenChange={setShowPromotionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPromotion ? 'Modifica Promozione' : 'Nuova Promozione'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome Promozione</Label>
              <Input 
                value={promotionForm.name} 
                onChange={(e) => setPromotionForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Es. Sconto Primavera 2026"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea 
                value={promotionForm.description} 
                onChange={(e) => setPromotionForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descrizione della promozione"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sconto %</Label>
                <Input 
                  type="number"
                  min="0"
                  max="100"
                  value={promotionForm.discount_percentage} 
                  onChange={(e) => setPromotionForm(prev => ({ ...prev, discount_percentage: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Applica a</Label>
                <Select value={promotionForm.applies_to} onValueChange={(v) => setPromotionForm(prev => ({ ...prev, applies_to: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i prodotti</SelectItem>
                    <SelectItem value="category">Categoria specifica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {promotionForm.applies_to === 'category' && (
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={promotionForm.category_code} onValueChange={(v) => setPromotionForm(prev => ({ ...prev, category_code: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.code} value={cat.code}>{cat.code} - {cat.name || cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch 
                checked={promotionForm.is_active} 
                onCheckedChange={(v) => setPromotionForm(prev => ({ ...prev, is_active: v }))}
              />
              <Label>Promozione attiva</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromotionModal(false)}>Annulla</Button>
            <Button className="btn-primary" onClick={handleSavePromotion}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Sala Riunioni */}
      <Dialog open={showRoomModal} onOpenChange={setShowRoomModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRoom ? 'Modifica Sala' : 'Nuova Sala'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome Sala</Label>
              <Input 
                value={roomForm.name} 
                onChange={(e) => setRoomForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Es. Sala Riunioni A"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Capacità (persone)</Label>
                <Input 
                  type="number"
                  min="1"
                  value={roomForm.capacity} 
                  onChange={(e) => setRoomForm(prev => ({ ...prev, capacity: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Durata Slot (minuti)</Label>
                <Input 
                  type="number"
                  min="15"
                  step="15"
                  value={roomForm.slot_duration_minutes} 
                  onChange={(e) => setRoomForm(prev => ({ ...prev, slot_duration_minutes: parseInt(e.target.value) || 60 }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Disponibile dalle</Label>
                <Input 
                  type="time"
                  value={roomForm.available_from} 
                  onChange={(e) => setRoomForm(prev => ({ ...prev, available_from: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Disponibile fino alle</Label>
                <Input 
                  type="time"
                  value={roomForm.available_to} 
                  onChange={(e) => setRoomForm(prev => ({ ...prev, available_to: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={roomForm.is_active} 
                onCheckedChange={(v) => setRoomForm(prev => ({ ...prev, is_active: v }))}
              />
              <Label>Sala attiva</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoomModal(false)}>Annulla</Button>
            <Button className="btn-primary" onClick={handleSaveRoom}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Categoria */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Modifica Categoria' : 'Nuova Categoria'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Codice (lettera)</Label>
                <Input 
                  value={categoryForm.code} 
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="Es. A"
                  maxLength={2}
                  disabled={!!editingCategory}
                />
              </div>
              <div className="space-y-2">
                <Label>Ordine</Label>
                <Input 
                  type="number"
                  min="0"
                  value={categoryForm.order} 
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nome Categoria</Label>
              <Input 
                value={categoryForm.label} 
                onChange={(e) => setCategoryForm(prev => ({ ...prev, label: e.target.value }))}
                placeholder="Es. PROGETTAZIONE"
              />
            </div>
            <div className="space-y-2">
              <Label>Colore</Label>
              <div className="flex gap-2">
                <Input 
                  type="color"
                  value={categoryForm.color} 
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                  className="w-16 h-10 p-1"
                />
                <Input 
                  value={categoryForm.color} 
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#FF7819"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>Annulla</Button>
            <Button className="btn-primary" onClick={handleSaveCategory}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== WORKFLOW SETTINGS COMPONENT ====================
const WorkflowSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('lead_statuses');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/settings/workflow');
      setConfig(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento configurazione workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (type, status, enabled) => {
    try {
      await api.put(`/settings/workflow/${type}/${status}`, { enabled });
      
      // Update local state
      setConfig(prev => ({
        ...prev,
        [`${type}es`]: prev[`${type}es`].map(s => 
          s.status === status ? { ...s, enabled } : s
        )
      }));
      
      toast.success(`Stato ${enabled ? 'abilitato' : 'disabilitato'}`);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleToggleNotification = async (key, enabled) => {
    try {
      await api.put(`/settings/workflow/notification/${key}`, { enabled });
      
      setConfig(prev => ({
        ...prev,
        notifications_enabled: {
          ...prev.notifications_enabled,
          [key]: enabled
        }
      }));
      
      toast.success(`Notifica ${enabled ? 'abilitata' : 'disabilitata'}`);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  if (loading) {
    return (
      <Card className="card-abitalo">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-[#737373]">Caricamento configurazione...</p>
        </CardContent>
      </Card>
    );
  }

  const notificationLabels = {
    lead_nuovo: { label: 'Nuovo Lead', desc: 'Notifica quando arriva un nuovo lead' },
    lead_assegnato: { label: 'Lead Assegnato', desc: 'Notifica al tecnico quando gli viene assegnato un lead' },
    appuntamento_reminder: { label: 'Promemoria Appuntamento', desc: 'Reminder 24h prima dell\'appuntamento' },
    preventivo_iniziato: { label: 'Preventivo Iniziato', desc: 'Notifica admin quando tecnico inizia preventivazione' },
    preventivo_stampato: { label: 'Preventivo Pronto', desc: 'Notifica quando preventivo è pronto' },
    contratto_da_validare: { label: 'Contratto da Validare', desc: 'Notifica admin per validazione contratto' },
    documenti_da_validare: { label: 'Documenti da Validare', desc: 'Notifica amministrazione per validazione allegati' },
    contratto_validato: { label: 'Contratto Validato', desc: 'Notifica quando contratto è approvato' },
    progetto_creato: { label: 'Progetto Creato', desc: 'Notifica creazione nuovo progetto' },
    milestone_da_fatturare: { label: 'Milestone da Fatturare', desc: 'Notifica amministrazione per fatturazione' },
    ordine_materiali: { label: 'Ordine Materiali', desc: 'Notifica quando viene inserito un ordine' },
    ordine_arredi: { label: 'Ordine Arredi', desc: 'Notifica per nuovo ordine arredi' },
    sal_aggiornato: { label: 'SAL Aggiornato', desc: 'Notifica aggiornamento avanzamento lavori' },
  };

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setActiveSection('lead_statuses')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            activeSection === 'lead_statuses' 
              ? 'bg-[#FF7819] text-white' 
              : 'bg-gray-100 text-[#737373] hover:bg-gray-200'
          }`}
        >
          Stati Lead
        </button>
        <button
          onClick={() => setActiveSection('project_statuses')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            activeSection === 'project_statuses' 
              ? 'bg-[#FF7819] text-white' 
              : 'bg-gray-100 text-[#737373] hover:bg-gray-200'
          }`}
        >
          Stati Progetto
        </button>
        <button
          onClick={() => setActiveSection('notifications')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            activeSection === 'notifications' 
              ? 'bg-[#FF7819] text-white' 
              : 'bg-gray-100 text-[#737373] hover:bg-gray-200'
          }`}
        >
          Notifiche Automatiche
        </button>
      </div>

      {/* Lead Statuses */}
      {activeSection === 'lead_statuses' && (
        <Card className="card-abitalo">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-[#FF7819]" />
              Stati Pipeline Lead
            </CardTitle>
            <CardDescription>
              Abilita o disabilita gli stati nel workflow dei lead. Gli stati disabilitati non appariranno nella pipeline.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {config?.lead_statuses?.map((status, idx) => (
                <div 
                  key={status.status}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                    status.enabled 
                      ? 'bg-white border-gray-200 hover:border-[#FF7819]/50' 
                      : 'bg-gray-50 border-gray-100 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: status.color }}
                    />
                    <div>
                      <p className="font-medium text-[#3C3C3C]">{status.label}</p>
                      <p className="text-xs text-[#999]">Codice: {status.status}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="text-xs">
                      Ordine: {status.order}
                    </Badge>
                    <Switch
                      checked={status.enabled}
                      onCheckedChange={(checked) => handleToggleStatus('lead-status', status.status, checked)}
                      disabled={['nuovo', 'convertito', 'perso'].includes(status.status)}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#999] mt-4">
              * Gli stati "Nuovo", "Convertito" e "Perso" non possono essere disabilitati.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Project Statuses */}
      {activeSection === 'project_statuses' && (
        <Card className="card-abitalo">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#FF7819]" />
              Stati Progetto
            </CardTitle>
            <CardDescription>
              Configura gli stati disponibili per i progetti.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {config?.project_statuses?.map((status, idx) => (
                <div 
                  key={status.status}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                    status.enabled 
                      ? 'bg-white border-gray-200 hover:border-[#FF7819]/50' 
                      : 'bg-gray-50 border-gray-100 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: status.color }}
                    />
                    <div>
                      <p className="font-medium text-[#3C3C3C]">{status.label}</p>
                      <p className="text-xs text-[#999]">Codice: {status.status}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="text-xs">
                      Ordine: {status.order}
                    </Badge>
                    <Switch
                      checked={status.enabled}
                      onCheckedChange={(checked) => {
                        // For now, just show toast - project statuses are more complex
                        toast.info('Modifica stati progetto disponibile prossimamente');
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notifications */}
      {activeSection === 'notifications' && (
        <Card className="card-abitalo">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#FF7819]" />
              Notifiche Automatiche
            </CardTitle>
            <CardDescription>
              Attiva o disattiva le notifiche email automatiche per ogni evento.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(notificationLabels).map(([key, info]) => {
                const isEnabled = config?.notifications_enabled?.[key] ?? true;
                return (
                  <div 
                    key={key}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      isEnabled 
                        ? 'bg-white border-gray-200' 
                        : 'bg-gray-50 border-gray-100 opacity-60'
                    }`}
                  >
                    <div>
                      <p className="font-medium text-[#3C3C3C]">{info.label}</p>
                      <p className="text-xs text-[#737373]">{info.desc}</p>
                    </div>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={(checked) => handleToggleNotification(key, checked)}
                    />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
